import { useAtomValue, useSetAtom } from "jotai";
import { Node } from "@xyflow/react";
import { SyntheticEvent, KeyboardEvent, useCallback, useState } from "react";
import { Typeahead } from "@zapier/design-system-beta";
import type { Key } from "@zapier/design-system-beta";
import { Text } from "@zapier/design-system";
import {
  actorsAtomFamily,
  nodeAtomFamily,
  takeSnapshotSetter,
  DefaultNodeData,
  ZapNodeData,
} from "@zapier/canvas-state";
import { ACTOR_TYPEAHEAD_LABEL } from "@zapier/canvas-constants";

import useUpdateNode from "../../../hooks/useUpdateNode";
import { generateActorColor } from "../../../utils/generateActorColor";

export function ActorTypeahead({
  isDisabled,
  id,
}: {
  isDisabled?: boolean;
  id: string;
}) {
  const node = useAtomValue(nodeAtomFamily(id)) as Node<
    DefaultNodeData | ZapNodeData
  >;
  const [search, setSearch] = useState<string>("");
  const allActors = useAtomValue(actorsAtomFamily(false));
  const actor = node?.data.actors?.[0] ?? null;

  const updateNodeAndSave = useUpdateNode();
  const takeSnapshot = useSetAtom(takeSnapshotSetter);

  const handleSelectionChange = useCallback(
    (selectedKey: Key | null) => {
      // selectedKey = null when you select the same option again
      const selectedActor = selectedKey
        ? allActors.find((a) => a.label === selectedKey)
        : null;

      // Selecting an option from Typeahead
      if (selectedActor) {
        takeSnapshot();

        updateNodeAndSave({
          id,
          data: {
            actors: [selectedActor],
          },
        });
      }

      setSearch("");
    },
    [allActors, id, takeSnapshot, updateNodeAndSave],
  );

  const handleEnterKeyPress = useCallback(
    (event: SyntheticEvent) => {
      const keyboardEvent = event as KeyboardEvent<HTMLInputElement>;

      if (keyboardEvent.key === "Enter") {
        const trimmedSearchTerm = search.trim();
        if (trimmedSearchTerm) {
          takeSnapshot();

          const existingActorInTypeahead = allActors.find(
            (actor) => actor.label === trimmedSearchTerm,
          );

          updateNodeAndSave({
            id,
            data: {
              actors: [
                existingActorInTypeahead ?? {
                  label: trimmedSearchTerm,
                  color: actor?.color ?? generateActorColor(trimmedSearchTerm),
                },
              ],
            },
          });
        }
      }
    },
    [actor?.color, allActors, id, search, takeSnapshot, updateNodeAndSave],
  );

  return (
    <>
      <Text type="SmallPrint2">{ACTOR_TYPEAHEAD_LABEL}</Text>
      <Typeahead
        key={actor?.label}
        aria-label="Add people involved"
        menuTrigger="focus"
        isDisabled={isDisabled}
        onInputChange={setSearch}
        onSelectionChange={handleSelectionChange}
        onKeyDown={handleEnterKeyPress}
        size="compact"
      >
        {allActors.map((actor) => (
          <Typeahead.Item key={actor.label} id={actor.label}>
            {actor.label}
          </Typeahead.Item>
        ))}
      </Typeahead>
    </>
  );
}
